import { useQuery } from "@tanstack/react-query";
import {
  isDailyGroupingAllowed,
  resolveReportPeriod,
  type ReportGroupBy,
  type ReportPreset,
} from "@opus-dt/shared-types";
import { api } from "@/lib/api-client";
import { useOrgTimezone } from "@/hooks/useOrgTimezone";
import type { ProductionReportResponse, ReportScopeKind } from "@/types/production-report.types";

// Spec 083 (T031) — a leitura do relatório.
//
// Só leitura: a tela não escreve nada (FR-031). Quem a INVALIDA são as escritas de produção, perda
// e ligação entre linhas, pelo PREFIXO `["production-report"]` — a combinação de período e
// agrupamento é infinita, e enumerá-la seria garantir esquecer uma.
//
// ⚠️ A chave carrega o período RESOLVIDO, nunca o preset. "30 dias" vira um par de datas diferente
// a cada virada de meia-noite, e cachear pelo preset devolveria ontem depois da virada.

export const productionReportKeyPrefix = ["production-report"] as const;

export interface ReportParams {
  scope: ReportScopeKind;
  lineId: string;
  from: string;
  to: string;
  groupBy: ReportGroupBy;
  /** Spec 084 — recorte e quadro ENTRAM na chave: os dois mudam todo número da resposta. */
  characteristicId?: string | null;
  value?: string | null;
  compareBy?: string | null;
}

/**
 * ⚠️ O indicador eleito NÃO entra na chave nem na requisição: a resposta já traz TODOS os
 * indicadores da etapa, e a eleição é escolha de exibição. Levá-lo junto trocava a chave a cada
 * troca do seletor e a tela inteira sumia para um "carregando" que devolvia exatamente os mesmos
 * números.
 */
export function useProductionReport(params: ReportParams | null) {
  return useQuery<ProductionReportResponse>({
    queryKey: [
      ...productionReportKeyPrefix,
      params?.scope,
      params?.lineId,
      params?.from,
      params?.to,
      params?.groupBy,
      params?.characteristicId ?? null,
      params?.value ?? null,
      params?.compareBy ?? null,
    ],
    enabled: params !== null,
    queryFn: () => {
      const p = params as ReportParams;
      const search = new URLSearchParams({
        scope: p.scope,
        lineId: p.lineId,
        from: p.from,
        to: p.to,
        groupBy: p.groupBy,
      });
      if (p.characteristicId && p.value) {
        search.set("characteristicId", p.characteristicId);
        search.set("value", p.value);
      }
      if (p.compareBy) search.set("compareBy", p.compareBy);
      return api.get<ProductionReportResponse>(`/api/v1/production-report?${search.toString()}`);
    },
  });
}

/**
 * Resolve preset + intervalo livre num par de datas civis, no fuso da ORGANIZAÇÃO.
 *
 * O fuso é o da fábrica, não o do navegador: o gestor pode abrir a tela de outro fuso e o dia de
 * produção continua sendo o dia da fábrica.
 */
export function useResolvedPeriod(
  preset: ReportPreset,
  custom: { from: string; to: string }
): { from: string; to: string } {
  const timezone = useOrgTimezone();
  return resolveReportPeriod(preset, timezone, custom);
}

/** O agrupamento diário some acima do teto — oferecer o que será recusado é pior que não oferecer. */
export function allowedGroupings(from: string, to: string): ReportGroupBy[] {
  const all: ReportGroupBy[] = ["day", "week", "month"];
  return isDailyGroupingAllowed(from, to) ? all : all.filter((g) => g !== "day");
}
